
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import procedimentos from '@/data/procedimentos.json';

interface HeaderProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  scrollToSection: (sectionId: string) => void;
  onOpenBooking: () => void;
}

// Mesma lista do rodapé, dos cards da home, do formulário e do sitemap.
const procedureCategories = procedimentos;

const Header: React.FC<HeaderProps> = ({ isMenuOpen, setIsMenuOpen, scrollToSection: _scrollToSection, onOpenBooking }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileProceduresOpen, setIsMobileProceduresOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const handleDepoimentos = () => {
    setIsMenuOpen(false);

    const scrollTo = () => {
      const el = document.getElementById('depoimentos');
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };

    if (scrollTo()) return;

    // Fora da homepage — navega para home com hash
    if (window.location.pathname !== '/') {
      navigate('/#depoimentos');
      return;
    }

    // Aguarda o módulo da seção terminar de carregar.
    const poll = setInterval(() => { if (scrollTo()) clearInterval(poll); }, 50);
    setTimeout(() => clearInterval(poll), 1500);
  };

  const openDropdown = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsDropdownOpen(true);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setIsDropdownOpen(false), 120);
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-dark-base/80 backdrop-blur-xl border-b border-white/5 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20 lg:h-24">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/simbolo-luis-brunello-sm.webp"
              alt="Logo Dr. Luis Brunello — Cirurgião Plástico em Curitiba"
              title="Dr. Luis Brunello — Cirurgia Plástica"
              className="h-10 sm:h-12 w-auto object-contain"
              width="47"
              height="112"
              loading="eager"
              decoding="async"
            />
            <div className="flex flex-col leading-none">
              <span className="font-display text-light-primary text-lg sm:text-xl font-normal uppercase" style={{ letterSpacing: '0.12em' }}>
                Luis Brunello
              </span>
              <span className="text-light-muted text-[11px] sm:text-xs tracking-[0.22em] uppercase mt-0.5" style={{ fontFamily: "'area-normal', sans-serif" }}>
                Cirurgia Plástica
              </span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden lg:flex items-center space-x-10">
            <Link to="/sobre" className="text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide link-hover">
              Sobre
            </Link>

            {/* Procedimentos dropdown */}
            <div
              className="relative"
              onMouseEnter={openDropdown}
              onMouseLeave={scheduleClose}
            >
              <button className="flex items-center gap-1 text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide link-hover">
                Procedimentos
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[760px] rounded-xl border border-white/8 shadow-2xl overflow-hidden"
                  style={{ background: 'rgba(8,22,28,0.97)', backdropFilter: 'blur(24px)' }}
                  onMouseEnter={openDropdown}
                  onMouseLeave={scheduleClose}
                >
                  {/* Arrow */}
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 border-l border-t border-white/8"
                    style={{ background: 'rgba(8,22,28,0.97)' }} />

                  <div className="grid grid-cols-3 gap-0 p-8">
                    {procedureCategories.map((cat) => (
                      <div key={cat.label} className="px-6 first:pl-0 last:pr-0 border-r border-white/5 last:border-r-0">
                        <p className="subtitle-wide text-[10px] mb-4" style={{ color: 'hsl(var(--color-gold))' }}>
                          {cat.label}
                        </p>
                        <ul className="space-y-2.5">
                          {cat.items.map((item) => (
                            <li key={item.slug}>
                              <Link
                                to={`/procedimentos/${item.slug}`}
                                onClick={() => setIsDropdownOpen(false)}
                                className="block text-sm text-light-muted hover:text-light-primary transition-colors py-0.5"
                              >
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/jornada" className="text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide link-hover">
              Jornada do Paciente
            </Link>
            <button onClick={handleDepoimentos} className="text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide link-hover">
              Depoimentos
            </button>
            <Link to="/blog" className="text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide link-hover">
              Blog
            </Link>
            <Button
              onClick={onOpenBooking}
              className="btn-pill btn-gold text-sm font-semibold"
            >
              Agendar Consulta
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-light-primary min-w-[48px] min-h-[48px] flex items-center justify-center -mr-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-dark-base/95 backdrop-blur-xl border-b border-white/5 max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col p-6 space-y-1">
              <Link
                to="/sobre"
                className="text-left text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide min-h-[48px] flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Sobre
              </Link>

              {/* Mobile Procedimentos accordion */}
              <div>
                <button
                  onClick={() => setIsMobileProceduresOpen(!isMobileProceduresOpen)}
                  className="w-full text-left text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide min-h-[48px] flex items-center justify-between"
                >
                  Procedimentos
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isMobileProceduresOpen ? 'rotate-180' : ''}`} />
                </button>

                {isMobileProceduresOpen && (
                  <div className="pl-4 pb-2 space-y-4">
                    {procedureCategories.map((cat) => (
                      <div key={cat.label}>
                        <p className="subtitle-wide text-[9px] mb-2 mt-3" style={{ color: 'hsl(var(--color-gold))' }}>
                          {cat.label}
                        </p>
                        <ul className="space-y-0.5">
                          {cat.items.map((item) => (
                            <li key={item.slug}>
                              <Link
                                to={`/procedimentos/${item.slug}`}
                                onClick={() => { setIsMenuOpen(false); setIsMobileProceduresOpen(false); }}
                                className="block text-sm text-light-muted hover:text-light-primary transition-colors py-1.5"
                              >
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/jornada"
                className="text-left text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide min-h-[48px] flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Jornada do Paciente
              </Link>
              <button onClick={handleDepoimentos} className="text-left text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide min-h-[48px] flex items-center">
                Depoimentos
              </button>
              <Link
                to="/blog"
                className="text-left text-light-secondary hover:text-light-primary transition-colors font-medium text-sm tracking-wide min-h-[48px] flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
              <div className="pt-2">
                <Button
                  onClick={onOpenBooking}
                  className="btn-pill btn-gold text-sm font-semibold w-full min-h-[48px]"
                >
                  Agendar Consulta
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
